/**
 * ConfirmModal Component
 * 
 * A confirmation dialog that asks the user to confirm an action.
 * Used for destructive actions like deleting an invoice.
 * 
 * Uses React Portal to render at document body level, ensuring:
 * - Proper z-index stacking above all other content
 * - No interference from parent element styling
 * - Correct fixed positioning relative to viewport
 * 
 * Variants:
 * - 'primary': Grey confirmation button with warning triangle icon
 * - 'danger': Red-bordered confirmation button with exclamation circle icon (for delete actions)
 */
import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';

interface ConfirmModalProps {
  /** Whether the modal is visible */
  isOpen: boolean;
  /** The title of the modal */
  title: string;
  /** The message to display */
  message: string;
  /** Text for the confirm button */
  confirmLabel?: string;
  /** Text for the cancel button */
  cancelLabel?: string;
  /** Variant for the confirm button (affects color and icon) */
  confirmVariant?: 'primary' | 'danger';
  /** Called when user clicks confirm */
  onConfirm: () => void;
  /** Called when user clicks cancel or closes the modal */
  onCancel: () => void;
  /** Whether the confirm action is in progress */
  isLoading?: boolean;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmVariant = 'primary',
  onConfirm,
  onCancel,
  isLoading = false,
}) => {
  // Handle escape key to close modal
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape' && !isLoading) {
      onCancel();
    }
  }, [onCancel, isLoading]);

  // Add/remove escape key listener and body scroll lock
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
      
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = '';
      };
    }
  }, [isOpen, handleKeyDown]);

  // Don't render if not open
  if (!isOpen) return null;

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    // Only close if clicking directly on backdrop, not modal content
    if (e.target === e.currentTarget && !isLoading) {
      onCancel();
    }
  };

  // Handle confirm click with stopPropagation
  const handleConfirmClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!isLoading) {
      onConfirm();
    }
  };

  // Handle cancel click with stopPropagation
  const handleCancelClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!isLoading) {
      onCancel();
    }
  };

  // Warning Triangle Icon (for primary variant)
  const WarningTriangleIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path 
        d="M12 9V13M12 17H12.01M10.29 3.86L1.82 18C1.64 18.3 1.55 18.64 1.55 19C1.55 19.36 1.64 19.7 1.82 20C2 20.3 2.26 20.56 2.58 20.74C2.9 20.92 3.26 21.01 3.62 21H20.38C20.74 21.01 21.1 20.92 21.42 20.74C21.74 20.56 22 20.3 22.18 20C22.36 19.7 22.45 19.36 22.45 19C22.45 18.64 22.36 18.3 22.18 18L13.71 3.86C13.53 3.56 13.27 3.32 12.95 3.15C12.63 2.98 12.27 2.89 11.91 2.89C11.55 2.89 11.19 2.98 10.87 3.15C10.55 3.32 10.29 3.56 10.11 3.86H10.29Z" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
      />
    </svg>
  );

  // Exclamation Circle Icon (for danger variant)
  const ExclamationCircleIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
      <path d="M12 8V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      <circle cx="12" cy="16" r="1" fill="currentColor"/>
    </svg>
  );

  // Modal content
  const modalContent = (
    <div 
      className="modal-overlay" 
      onClick={handleBackdropClick}
      data-testid="confirm-modal"
      role="presentation"
    >
      <div 
        className={`modal-container modal-variant-${confirmVariant}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
        aria-modal="true"
      >
        {/* Header row: icon, title, close button - all aligned */}
        <div className="modal-header-row">
          <span className={`modal-icon modal-icon-${confirmVariant}`}>
            {confirmVariant === 'danger' ? <ExclamationCircleIcon /> : <WarningTriangleIcon />}
          </span>
          <h2 id="modal-title" className="modal-header-title">{title}</h2>
          <button 
            type="button"
            className="modal-close-btn"
            onClick={handleCancelClick}
            disabled={isLoading}
            aria-label="Close"
            title="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        
        {/* Separator line with margins */}
        <div className="modal-separator"></div>

        {/* Body with message */}
        <div className="modal-body">
          <p id="modal-description" className="modal-message">{message}</p>
        </div>

        {/* Footer with action buttons */}
        <div className="modal-footer">
          <button 
            type="button"
            className="modal-btn modal-btn-cancel" 
            onClick={handleCancelClick}
            disabled={isLoading}
          >
            {cancelLabel}
          </button>
          <button 
            type="button"
            className={`modal-btn modal-btn-confirm modal-btn-${confirmVariant}`}
            onClick={handleConfirmClick}
            disabled={isLoading}
            data-testid="confirm-button"
            autoFocus
          >
            {isLoading ? 'Processing...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );

  // Use portal to render at document body level
  return createPortal(modalContent, document.body);
};

export default ConfirmModal;

